<script setup lang="ts">
import { useI18n } from "vue-i18n";
import Trans from "~/modules/translations";
const { t } = useI18n();

const route = useRoute();

import HeaderSkeleton from "./HeaderSkeleton.vue";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { MarketplaceApi } from "~/api/marketplace";
import { Scarcity, MercureEventType, MarketType, NftSkill, AuctionStatus } from "fungi-types";
import type { IAuctionItem } from "~/common/interfaces/IMarketplace";

import { usePrimaryMarketplace } from "~/stores/storePrimaryMarketplace";
import { auctionApi } from "~/api/auction";

const contents = [
  {
    background: "/images/headers/marketplace.jpg",
    title: t("pages.marketplace.headers.primary.title"),
    description: t("pages.marketplace.headers.primary.description"),
  },
  {
    background: "/images/headers/marketplace.jpg",
    title: t("pages.marketplace.headers.secondary.title"),
    description: t("pages.marketplace.headers.secondary.description"),
  },
];

const api = new MarketplaceApi(MarketType.PRIMARY);
const auctions = ref<any[]>([]);
const store = usePrimaryMarketplace();

const content = computed(() => {
  switch (route.name) {
    case "NewCards":
      return contents[0];
    case "Trade":
      return contents[1];
    default:
      return contents[0];
  }
});

onMounted(async () => {
  const filters = { scarcity: [Scarcity.PLATINUM] };

  const items = await auctionApi.auctions.list.query({
    scarcities: [Scarcity.PLATINUM],
    markets: [MarketType.PRIMARY],
    page: 1,
    statuses: [AuctionStatus.IN_PROGRESS],
  });
  auctions.value = items.result;
});

const showHeader = ref(false);
</script>

<template>
  <HeaderSkeleton
    :showHeader="showHeader"
    :background="content.background"
    :title="content.title"
    :subtitle="content.description"
  >
    <template #featuring>
      <div
        class="h-full w-full bg-cover bg-center bg-no-repeat flex justify-center flex-col z-10 relative"
        :style="{ backgroundImage: `url('/images/headers/platinum.jpg')` }"
      >
        <div
          class="flex md:flex-row flex-col mt-20 md:mt-32 w-full place-items-stretch mb-16 justify-between overflow-y-scroll md:overscroll-none scrollbar-hide"
        >
          <div class="items-center md:flex">
            <div
              class="flex flex-col gap-3 md:gap-6 pl-5 md:px-10 mb-5 md:pb-20 md:min-w-[400px] lg:min-w-[600px] max-w-[800px] mr-4 md:mr-10"
            >
              <div class="text-xl md:text-4xl font-semibold">
                {{ t("pages.marketplace.headers.featuring.platinum.title") }}
              </div>
              <div class="flex flex-col gap-1">
                <div class="font-semibold md:text-lg">
                  {{ t("pages.marketplace.headers.featuring.platinum.subtitle") }}
                </div>
                <div class="text-xs md:text-sm">
                  {{ t("pages.marketplace.headers.featuring.platinum.description") }}
                </div>
              </div>
            </div>
          </div>
          <div
            class="place-self-start col-span-2 overflow-y-scroll scrollbar-hide pl-2 md:pl-0 w-full"
          >
            <div class="relative grid grid-flow-col">
              <div
                v-for="(auction, index) in auctions.slice(0, 4)"
                :key="`${auction.cuid}-${auction.lastBidAt}-${auction.status}-sticky-platinum`"
                class="w-56 mr-6"
              >
                <AuctionItem :key="index" :item="auction" />
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row gap-2 px-4 text-sm relative">
          <router-link
            :to="Trans.i18nRoute({ name: 'NewCards', params: $route.params })"
            class="p-2 px-4 bg-slate-700 border-[1px] border-slate-600 relative z-10 border-b-0 market-type transition-all"
            >{{ t("pages.marketplace.headers.primary.tab") }}</router-link
          >
          <router-link
            :to="Trans.i18nRoute({ name: 'Trade', params: $route.params })"
            class="p-2 px-4 bg-slate-700 border-[1px] border-slate-600 relative z-10 border-b-0 market-type transition-all"
            >{{ t("pages.marketplace.headers.secondary.tab") }}</router-link
          >
        </div>
        <div class="h-[1px] bg-slate-600 w-full absolute bottom-0 z-20"></div>
      </div>
    </template>
  </HeaderSkeleton>
</template>

<style scoped>
.router-link-exact-active {
  @apply !border-slate-600 !bg-black transition-colors z-30;
}

.market-type:hover {
  @apply hover:bg-slate-800;
}

.router-link-exact-active.market-type:hover {
  @apply hover:bg-slate-700;
}
</style>
